import { useState, useMemo, useCallback } from 'react';
import type { Case, SalesforceCase } from '../types';

export type DashboardSection = 'new' | 'queue' | 'approved' | 'uploaded' | 'returned' | 'issues' | 'other';
export type IssueFilter = 'all' | 'lost' | 'found';

interface PaginationState {
  page: number;
  pageSize: number;
}

interface UseDashboardStateProps {
  cases: Case[];
  salesforceCases: SalesforceCase[];
  excludeCaseNumbers?: string[]; // Additional case numbers to exclude from "New in SF"
}

const DEFAULT_PAGE_SIZE = 15;
const STORAGE_KEY = 'dashboard_active_section';

const getInitialSection = (): DashboardSection => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && ['new', 'queue', 'approved', 'uploaded', 'returned', 'issues', 'other'].includes(stored)) {
    return stored as DashboardSection;
  }
  return 'queue';
};

export function useDashboardState({ cases, salesforceCases }: UseDashboardStateProps) {
  // Active section (persisted to localStorage)
  const [activeSection, setActiveSectionState] = useState<DashboardSection>(getInitialSection);
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination per section
  const [pagination, setPagination] = useState<Record<DashboardSection, PaginationState>>({
    new: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
    queue: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
    approved: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
    uploaded: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
    returned: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
    issues: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
    other: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
  });

  // Selections per section
  const [selections, setSelections] = useState<Record<DashboardSection, Set<string>>>({
    new: new Set(),
    queue: new Set(),
    approved: new Set(),
    uploaded: new Set(),
    returned: new Set(),
    issues: new Set(),
    other: new Set(),
  });

  // Get local case numbers for filtering SF cases
  const localCaseNumbers = useMemo(
    () => new Set(cases.map((c) => c.case_number)),
    [cases]
  );

  // Convert Salesforce cases to Case format (those not yet downloaded)
  const newInSalesforce = useMemo(
    () => salesforceCases
      .filter((sc) => !localCaseNumbers.has(sc.case_number))
      .map((sc): Case => ({
        id: sc.salesforce_id,
        case_number: sc.case_number,
        salesforce_id: sc.salesforce_id,
        type_of_analysis: sc.type_of_analysis,
        record_type: sc.record_type_name,
        status: 'pending',
        created_at: sc.last_modified || '',
        updated_at: sc.last_modified || '',
        last_modified: sc.last_modified,
        retry_count: 0,
      })),
    [salesforceCases, localCaseNumbers]
  );

  // Filter cases by status
  const translationQueue = useMemo(
    () => cases.filter((c) =>
      ['downloaded', 'failed', 'translating', 'translated', 'generating_pdf', 'ready_for_review'].includes(c.status)
    ),
    [cases]
  );

  const approvedCases = useMemo(
    () => cases.filter((c) => ['approved', 'uploading'].includes(c.status)),
    [cases]
  );

  const uploadedCases = useMemo(
    () => cases.filter((c) => c.status === 'uploaded'),
    [cases]
  );

  const returnedCases = useMemo(
    () => cases.filter((c) => c.status === 'returned'),
    [cases]
  );

  const lostCases = useMemo(
    () => cases.filter((c) => c.status === 'lost'),
    [cases]
  );

  const foundCases = useMemo(
    () => cases.filter((c) => c.status === 'found'),
    [cases]
  );

  const otherCases = useMemo(
    () => cases.filter((c) => c.status === 'other'),
    [cases]
  );

  // Issue cases (lost + found only, returned is separate)
  const issueCases = useMemo(() => {
    switch (issueFilter) {
      case 'lost':
        return lostCases;
      case 'found':
        return foundCases;
      default:
        return [...lostCases, ...foundCases];
    }
  }, [issueFilter, lostCases, foundCases]);

  // Counts for sidebar badges
  const counts = useMemo(() => ({
    new: newInSalesforce.length,
    queue: translationQueue.length,
    approved: approvedCases.length,
    uploaded: uploadedCases.length,
    returned: returnedCases.length,
    issues: lostCases.length + foundCases.length,
    other: otherCases.length,
  }), [newInSalesforce, translationQueue, approvedCases, uploadedCases, returnedCases, lostCases, foundCases, otherCases]);

  // Issue sub-counts (lost + found only)
  const issueCounts = useMemo(() => ({
    lost: lostCases.length,
    found: foundCases.length,
  }), [lostCases, foundCases]);

  // Get cases for active section
  const sectionCases = useMemo(() => {
    switch (activeSection) {
      case 'new':
        return newInSalesforce;
      case 'queue':
        return translationQueue;
      case 'approved':
        return approvedCases;
      case 'uploaded':
        return uploadedCases;
      case 'returned':
        return returnedCases;
      case 'issues':
        return issueCases;
      case 'other':
        return otherCases;
      default:
        return [];
    }
  }, [activeSection, newInSalesforce, translationQueue, approvedCases, uploadedCases, returnedCases, issueCases, otherCases]);

  // Filter by search query
  const filteredCases = useMemo(() => {
    if (!searchQuery.trim()) return sectionCases;
    const query = searchQuery.toLowerCase();
    return sectionCases.filter((c) =>
      c.case_number.toLowerCase().includes(query) ||
      c.type_of_analysis?.toLowerCase().includes(query) ||
      c.record_type?.toLowerCase().includes(query)
    );
  }, [sectionCases, searchQuery]);

  // Paginated cases - paginate 'uploaded' and 'other' sections, show all for others
  const paginatedCases = useMemo(() => {
    if (activeSection !== 'uploaded' && activeSection !== 'other') {
      // No pagination for non-uploaded/non-other sections - show all
      return filteredCases;
    }
    const { page, pageSize } = pagination[activeSection];
    const start = (page - 1) * pageSize;
    return filteredCases.slice(start, start + pageSize);
  }, [filteredCases, pagination, activeSection]);

  // Current pagination state
  const currentPagination = pagination[activeSection];
  const currentSelection = selections[activeSection];

  // Pagination helpers
  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      [activeSection]: { ...prev[activeSection], page },
    }));
  }, [activeSection]);

  const setPageSize = useCallback((pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      [activeSection]: { ...prev[activeSection], pageSize, page: 1 },
    }));
  }, [activeSection]);

  // Selection helpers
  const toggleSelection = useCallback((caseNumber: string, selected: boolean) => {
    setSelections((prev) => {
      const newSet = new Set(prev[activeSection]);
      if (selected) {
        newSet.add(caseNumber);
      } else {
        newSet.delete(caseNumber);
      }
      return { ...prev, [activeSection]: newSet };
    });
  }, [activeSection]);

  const selectAll = useCallback((cases: Case[], selected: boolean) => {
    setSelections((prev) => ({
      ...prev,
      [activeSection]: selected ? new Set(cases.map((c) => c.case_number)) : new Set(),
    }));
  }, [activeSection]);

  const clearSelection = useCallback(() => {
    setSelections((prev) => ({
      ...prev,
      [activeSection]: new Set(),
    }));
  }, [activeSection]);

  // Wrapper to persist section to localStorage
  const setActiveSection = useCallback((section: DashboardSection) => {
    setActiveSectionState(section);
    localStorage.setItem(STORAGE_KEY, section);
  }, []);

  // Reset pagination when section changes
  const changeSection = useCallback((section: DashboardSection) => {
    setActiveSection(section);
    setSearchQuery('');
    if (section !== 'issues') {
      setIssueFilter('all');
    }
  }, [setActiveSection]);

  return {
    // Section state
    activeSection,
    setActiveSection: changeSection,
    issueFilter,
    setIssueFilter,
    searchQuery,
    setSearchQuery,

    // Case data
    counts,
    issueCounts,
    filteredCases,
    paginatedCases,
    totalFiltered: filteredCases.length,

    // Raw case lists (for actions that need specific statuses)
    newInSalesforce,
    translationQueue,
    approvedCases,
    uploadedCases,
    returnedCases,
    lostCases,
    foundCases,
    otherCases,

    // Pagination (for uploaded and other sections)
    currentPage: currentPagination.page,
    pageSize: currentPagination.pageSize,
    totalPages: (activeSection === 'uploaded' || activeSection === 'other')
      ? Math.ceil(filteredCases.length / currentPagination.pageSize)
      : 1,
    setPage,
    setPageSize,
    showPagination: activeSection === 'uploaded' || activeSection === 'other',

    // Selection
    selectedCases: currentSelection,
    toggleSelection,
    selectAll,
    clearSelection,
  };
}
